
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Search } from 'lucide-react';
import { ReferralId } from '@/utils/referral/referralIdBank';

interface ReferralIdListProps {
  referralIds: ReferralId[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const ReferralIdList: React.FC<ReferralIdListProps> = ({
  referralIds,
  searchQuery,
  setSearchQuery
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Calculate pagination
  const totalPages = Math.ceil(referralIds.length / itemsPerPage);
  const paginatedIds = referralIds.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handlePreviousPage = () => {
    setCurrentPage(current => Math.max(current - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(current => Math.min(current + 1, totalPages));
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Reset to first page when searching
    setCurrentPage(1);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral ID List</CardTitle>
        <CardDescription>
          View and search all available referral IDs in the bank
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Search by referral code or instance ID..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </form>
        
        {/* Referral IDs Table */}
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referral Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Instance ID</TableHead>
                <TableHead>Created At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedIds.length > 0 ? (
                paginatedIds.map((referralId: ReferralId) => (
                  <TableRow key={referralId.id}>
                    <TableCell className="font-mono font-medium">
                      {referralId.code}
                    </TableCell>
                    <TableCell>
                      {referralId.isAssigned ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          Assigned
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Available
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {referralId.instanceId || '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(referralId.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    No referral IDs found
                    {searchQuery && " matching your search"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, referralIds.length)} of {referralIds.length} results
            </p>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralIdList;
